'use client';

import { useCallback, useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import { DataTestConfig, PRODUCT_NOTIFY_ME } from '@headless-workspace/config';
import { useUIContext } from '@headless-workspace/core-ui';
import {
    GlowButton,
    GlowClickable,
    GlowDivider,
    GlowIcon,
    GlowTitle,
    Icons,
    PropsWithLocale,
} from '@headless-workspace/glow-ds';
import { Component } from '@headless-workspace/typings';
import { getConnectedUserInfo } from '../../../server';
import { useUserInfo } from '../user/useUserInfo';
import { NotifyMeForm } from './NotifyMeForm';

type NotifyMeFormProps = PropsWithLocale & {
    productIds: string[];
};

export const NotifyMeModal: Component<NotifyMeFormProps> = ({ productIds, locale }) => {
    const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
    const t = useTranslations('Discover.subscribe.notifyMe');
    const { result: userInfoResult } = useUserInfo();
    const userInfo = getConnectedUserInfo(userInfoResult);

    const {
        menuModal: { close: closeMenu },
    } = useUIContext();

    const closeNotifyMeModal = useCallback(() => {
        closeMenu(PRODUCT_NOTIFY_ME);
    }, [closeMenu]);

    useEffect(() => {
        setIsSubmitted(false);
    }, [productIds]);

    return (
        <div
            id={PRODUCT_NOTIFY_ME}
            className={'h-fullViewport w-fullViewport tablet:w-sideMenuTablet flex flex-col bg-background-l3'}
        >
            <div
                className={
                    'flex flex-row justify-between items-center p-1 gap-1 border-b-medium border-b-border-subtle border-solid h-headerFixedNavDesktop'
                }
            >
                <GlowTitle text={t('modal.label.title')} fontSize={'title4'} className={'flex-1 text-left'} />
                <GlowClickable
                    onClick={closeNotifyMeModal}
                    content={{
                        icon: <GlowIcon Icon={Icons.Cross} type={'large'} className={'text-text-primary'} />,
                    }}
                />
            </div>
            {!isSubmitted ? (
                <NotifyMeForm
                    onSubmit={(e) => {
                        setIsSubmitted(e);
                    }}
                    productIds={productIds}
                    locale={locale}
                    defaultEmail={userInfo?.email}
                    onCloseModal={closeNotifyMeModal}
                />
            ) : (
                <div className={'flex flex-1 flex-col'}>
                    <div
                        className={'flex flex-1 flex-col gap-0.5 p-1'}
                        data-testid={DataTestConfig.testIds.notifyMeConfirmMessage}
                    >
                        <GlowIcon Icon={Icons.NotifyMe} type={'xxLarge'} />
                    </div>
                    <GlowDivider />
                    <div className={'flex items-center justify-center p-1 flex-col gap-0.5'}>
                        <GlowButton
                            className={'flex-1 w-full'}
                            label={t('modal.action.close')}
                            variant={'primary'}
                            onClick={closeNotifyMeModal}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};
